import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { zaloApi } from '@/features/messenger/api/zalo.api'
import {
  findDeliveredUnsent,
  newPendingId,
  toFailedId,
} from '@/features/messenger/utils/localMessage'
import type {
  ChatInfo,
  MessageInfo,
  MediaType,
  MessageStatus,
} from '@/features/messenger/types/messenger.types'
import type {
  ZaloOAStatusResponse,
  ZaloChatInfo,
  ZaloMessageInfo,
  ZaloSendTextRequest,
  ZaloSendImageRequest,
  ZaloSendDocumentRequest,
  WsZaloNewMessagePayload,
  WsZaloMessageSentPayload,
} from '@/features/messenger/types/zalo.types'

// ===================== Mappers: Zalo → shared types =====================

function mapZaloChatToChatInfo(chat: ZaloChatInfo): ChatInfo {
  return {
    jid: chat.userId,
    name: chat.displayName || chat.phone || chat.userId,
    lastMessageTime: chat.lastMessageTime,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
  }
}

function mapZaloMsgType(msgType: string): MediaType {
  switch (msgType) {
    case 'text':
      return 'text'
    case 'image':
      return 'image'
    case 'file':
      return 'document'
    case 'sticker':
      return 'sticker'
    case 'gif':
      return 'image'
    case 'audio':
      return 'audio'
    case 'video':
      return 'video'
    default:
      return 'text'
  }
}

function mapZaloMessageToMessageInfo(msg: ZaloMessageInfo): MessageInfo {
  const attachment = msg.attachments?.[0]
  return {
    id: msg.id,
    chatJid: msg.chatUserId,
    senderJid: msg.senderId,
    content: msg.content,
    timestamp: msg.timestamp,
    isFromMe: msg.isFromOa,
    mediaType: mapZaloMsgType(msg.msgType),
    status: (msg.status || 'sent') as MessageStatus,
    isReadByManager: msg.isReadByManager,
    url: attachment?.fileUrl,
    filename: attachment?.fileName,
    fileLength: attachment?.fileSize,
    createdAt: msg.createdAt,
    updatedAt: msg.updatedAt,
  }
}

// ===================== Store =====================

export const useZaloStore = defineStore('zalo', () => {
  // OA state
  const oaStatus = ref<ZaloOAStatusResponse | null>(null)
  const isLoadingStatus = ref(false)
  const isConnected = computed(() => oaStatus.value?.isActive === true)
  const currentOaId = computed(() => oaStatus.value?.oaId ?? null)
  // DEV-1681: сбой чтения статуса — не «OA не подключена». HTTP-код (0 — ответа нет)
  // отличает выключенный на сервере Zalo (503) от прочих сбоев.
  const statusErrorCode = ref<number | null>(null)

  // Chats
  const chats = ref<ZaloChatInfo[]>([])
  const chatsTotal = ref(0)
  const isLoadingChats = ref(false)
  // DEV-1675: непривязанные чаты — из них руководитель привязывает чат к клиенту.
  const unboundChats = ref<ZaloChatInfo[]>([])

  // Active chat / messages
  const currentUserId = ref<string | null>(null)
  const currentChatInfo = ref<ChatInfo | null>(null)
  const messages = ref<MessageInfo[]>([])
  const messagesTotal = ref(0)
  const isLoadingMessages = ref(false)

  // Errors
  const error = ref<string | null>(null)

  // ===================== OA =====================

  // DEV-1675: OA у компании одна — сервер отдаёт её статус, не подключена — isActive=false.
  async function loadOAStatus() {
    isLoadingStatus.value = true
    error.value = null
    statusErrorCode.value = null
    try {
      oaStatus.value = await zaloApi.getOAStatus()
    } catch (e) {
      error.value = String(e)
      statusErrorCode.value = (e as { response?: { status?: number } })?.response?.status ?? 0
      oaStatus.value = null
    } finally {
      isLoadingStatus.value = false
    }
  }

  async function getConnectUrl(redirectUri: string, state: string) {
    return zaloApi.getConnectUrl(redirectUri, state)
  }

  async function connect(authCode: string, redirectUri: string) {
    error.value = null
    try {
      const resp = await zaloApi.connect({
        authorization_code: authCode,
        redirect_uri: redirectUri,
      })
      oaStatus.value = {
        oaId: resp.oaId,
        oaName: resp.oaName,
        isActive: true,
        tokenExpiresAt: '',
      }
      return resp
    } catch (e) {
      error.value = String(e)
      throw e
    }
  }

  // Ошибку отдаём наверх: отказ отключения — не «OA не подключена», она работает дальше.
  async function disconnect() {
    await zaloApi.disconnect()
    oaStatus.value = null
    resetChat()
  }

  // ===================== Chats =====================

  async function loadChats(params?: {
    clientId?: string
    search?: string
    offset?: number
    limit?: number
  }) {
    isLoadingChats.value = true
    error.value = null
    try {
      const response = await zaloApi.getChats({
        clientId: params?.clientId,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
        search: params?.search,
      })
      chats.value = response.data ?? []
      chatsTotal.value = Number(response.pagination?.total) || 0
    } catch (e) {
      // Как у Telegram (DEV-2227): чужой список после ошибки не показываем.
      chats.value = []
      chatsTotal.value = 0
      error.value = String(e)
    } finally {
      isLoadingChats.value = false
    }
  }

  // DEV-1675: чат клиента — по client_id: привязку пишет сервер (ключ Zalo, автолид,
  // ручная привязка). Ошибку не глотаем: «чата нет» и «не нашли» — разные экраны,
  // иначе руководитель привязал бы клиенту второй чат. Открывает чат вкладка — она
  // знает, не ушли ли с карточки, пока шёл запрос.
  async function findClientChatId(clientId: string): Promise<string | null> {
    const response = await zaloApi.getChats({ clientId, limit: 1 })
    return response.data?.[0]?.userId ?? null
  }

  // Последние непривязанные чаты; выбор фильтрует их по имени у себя. Ошибку не
  // глотаем: пустой выбор выглядел бы как «привязывать нечего».
  // ponytail: 50 свежих по последнему сообщению или подписке — автолид привязывает
  // почти каждый чат; старее не найти, тогда — серверный search в ListChats.
  async function loadUnboundChats() {
    unboundChats.value = []
    const response = await zaloApi.getChats({ unbound: true, limit: 50 })
    unboundChats.value = response.data ?? []
  }

  async function bindChat(userId: string, clientId: string): Promise<boolean> {
    try {
      await zaloApi.bindChatToClient(userId, clientId)
      unboundChats.value = unboundChats.value.filter((c) => c.userId !== userId)
      return true
    } catch (e) {
      error.value = String(e)
      return false
    }
  }

  // DEV-2472: перенос чата с лида-дубля на карточку клиента. Ошибку отдаём наверх:
  // модалка покажет текст сервера и оставит повтор — повтор доводит прерванный перенос.
  async function moveChat(userId: string, fromClientId: string, toClientId: string) {
    await zaloApi.moveChatToClient(userId, fromClientId, toClientId)
  }

  // ===================== Messages =====================

  async function loadMessages(userId: string, params?: { offset?: number }) {
    isLoadingMessages.value = true
    error.value = null
    try {
      const response = await zaloApi.getMessages(userId, {
        limit: 50,
        offset: params?.offset ?? 0,
      })
      // Пока шёл запрос, открыли другой чат: чужая лента подменила бы его шапку.
      if (userId !== currentUserId.value) return
      const data = (response.data ?? []).map(mapZaloMessageToMessageInfo).slice().reverse()
      if (params?.offset && params.offset > 0) {
        messages.value = [...data, ...messages.value]
      } else {
        messages.value = data
      }
      messagesTotal.value = Number(response.pagination?.total) || 0
      currentChatInfo.value = response.chatInfo ? mapZaloChatToChatInfo(response.chatInfo) : null
    } catch (e) {
      error.value = String(e)
    } finally {
      isLoadingMessages.value = false
    }
  }

  async function setCurrentChat(userId: string) {
    currentUserId.value = userId
    messages.value = []
    messagesTotal.value = 0
    currentChatInfo.value = null
    await loadMessages(userId)
  }

  function resetChat() {
    currentUserId.value = null
    messages.value = []
    messagesTotal.value = 0
    currentChatInfo.value = null
  }

  // ===================== Send =====================

  async function sendText(userId: string, text: string) {
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: userId,
      senderJid: currentOaId.value ?? '',
      content: text,
      timestamp: now,
      isFromMe: true,
      mediaType: 'text',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      const req: ZaloSendTextRequest = { user_id: userId, message: text }
      await zaloApi.sendText(req)
    } catch (e) {
      // DEV-2492. Текст не теряем, а пузырь «Не отправлено» — и есть сообщение
      // об ошибке, которого у Zalo не было вовсе.
      messages.value = messages.value.map((m) =>
        m.id === optimistic.id ? { ...m, id: toFailedId(m.id), status: 'failed' } : m,
      )
      error.value = String(e)
    }
  }

  // DEV-2492. «Повторить» убирает неотправленный пузырь — текст уйдёт новой отправкой.
  function discardMessage(id: string) {
    messages.value = messages.value.filter((m) => m.id !== id)
  }

  async function sendImage(userId: string, imageUrl: string, caption?: string) {
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: userId,
      senderJid: currentOaId.value ?? '',
      content: caption || '',
      timestamp: now,
      isFromMe: true,
      mediaType: 'image',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      const req: ZaloSendImageRequest = { user_id: userId, image: imageUrl }
      if (caption) req.caption = caption
      await zaloApi.sendImage(req)
    } catch (e) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id)
      error.value = String(e)
    }
  }

  async function sendDocument(userId: string, docUrl: string, fileName?: string) {
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: userId,
      senderJid: currentOaId.value ?? '',
      content: fileName || '',
      timestamp: now,
      isFromMe: true,
      mediaType: 'document',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      const req: ZaloSendDocumentRequest = { user_id: userId, document: docUrl }
      if (fileName) req.file_name = fileName
      await zaloApi.sendDocument(req)
    } catch (e) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id)
      error.value = String(e)
    }
  }

  async function markAsRead(userId: string, messageId: string) {
    try {
      await zaloApi.markAsRead({ user_id: userId, message_id: messageId })
    } catch (e) {
      error.value = String(e)
    }
  }

  // ===================== WebSocket handlers =====================
  // DEV-1675: OA одна, события по oa_id не фильтруем — только по открытому чату.

  function addMessageFromWs(payload: WsZaloNewMessagePayload) {
    if (payload.chat_user_id !== currentUserId.value) return

    const alreadyExists = messages.value.some((m) => m.id === payload.id)
    if (alreadyExists) return

    const msg: MessageInfo = {
      id: payload.id,
      chatJid: payload.chat_user_id,
      senderJid: payload.sender_id,
      content: payload.content,
      timestamp: payload.timestamp,
      isFromMe: payload.is_from_oa,
      mediaType: mapZaloMsgType(payload.msg_type),
      status: 'delivered',
      createdAt: payload.timestamp,
      updatedAt: payload.timestamp,
    }

    messages.value = [...messages.value, msg]
  }

  function handleMessageSent(payload: WsZaloMessageSentPayload) {
    if (payload.chat_user_id !== currentUserId.value) return

    let pendingIdx = messages.value.findIndex(
      (m) => m.id.startsWith('pending-') && m.content === payload.content,
    )
    // DEV-2492. Запрос упал, а сообщение ушло: эхо в коротком окне подтверждает
    // «Не отправлено», иначе у доставленного так и висела бы ошибка.
    if (pendingIdx === -1) {
      pendingIdx = findDeliveredUnsent(messages.value, payload.content, payload.timestamp)
    }
    const pendingMsg = messages.value[pendingIdx]
    if (pendingIdx !== -1 && pendingMsg) {
      const confirmed: MessageInfo = {
        ...pendingMsg,
        id: payload.id,
        status: 'sent',
        timestamp: payload.timestamp,
      }
      messages.value = [
        ...messages.value.slice(0, pendingIdx),
        confirmed,
        ...messages.value.slice(pendingIdx + 1),
      ]
    }
  }

  return {
    // OA
    oaStatus,
    isLoadingStatus,
    isConnected,
    currentOaId,
    statusErrorCode,
    loadOAStatus,
    getConnectUrl,
    connect,
    disconnect,
    // Chats
    chats,
    chatsTotal,
    isLoadingChats,
    loadChats,
    findClientChatId,
    unboundChats,
    loadUnboundChats,
    bindChat,
    moveChat,
    // Messages
    currentUserId,
    currentChatInfo,
    messages,
    messagesTotal,
    isLoadingMessages,
    loadMessages,
    setCurrentChat,
    resetChat,
    // Send
    sendText,
    discardMessage,
    sendImage,
    sendDocument,
    markAsRead,
    // WS
    addMessageFromWs,
    handleMessageSent,
    // Errors
    error,
  }
})
